// Cliente de la API. Un metodo por endpoint, tipado, y un solo lugar donde se
// decide que hacer con los errores.
//
// Las rutas son relativas a proposito: en produccion el mismo Worker sirve la
// web y la API, y en desarrollo el proxy de Vite las manda a wrangler dev.

export class ErrorApi extends Error {
  constructor(
    readonly estado: number,
    mensaje: string,
    /** El cuerpo entero de la respuesta, para errores que traen datos (ver `duplicadoDe`). */
    readonly cuerpo: unknown = null,
  ) {
    super(mensaje);
    this.name = 'ErrorApi';
  }
}

async function pedir<T>(ruta: string, opciones: RequestInit = {}): Promise<T> {
  // Aislado por ruta: ninguna acción de la muestra llega a la API del grupo.
  if (typeof window !== 'undefined' && window.location.pathname === '/muestra') {
    const { pedirMuestra } = await import('../showroom/datos');
    return pedirMuestra<T>(ruta, opciones);
  }
  let respuesta: Response;
  try {
    respuesta = await fetch(`/api${ruta}`, {
      ...opciones,
      headers: { 'content-type': 'application/json', ...opciones.headers },
      credentials: 'same-origin',
    });
  } catch {
    // Sin red: en la cancha pasa seguido.
    throw new ErrorApi(0, 'No hay conexion. Revisa la senal y volve a intentar.');
  }

  const cuerpo = (await respuesta.json().catch(() => null)) as { error?: string } | T | null;

  if (!respuesta.ok) {
    const mensaje =
      (cuerpo && typeof cuerpo === 'object' && 'error' in cuerpo && cuerpo.error) ||
      `Algo fallo (${respuesta.status})`;
    throw new ErrorApi(respuesta.status, String(mensaje), cuerpo);
  }

  return cuerpo as T;
}

const postear = <T>(ruta: string, datos?: unknown) =>
  pedir<T>(ruta, { method: 'POST', body: datos === undefined ? undefined : JSON.stringify(datos) });

// ── Tipos ─────────────────────────────────────────────────────────────────

export interface Salud {
  ok: boolean;
  jugadores: number;
  partidos: number;
  version: string;
  secretos_cargados: boolean;
}

export interface JugadorPublico {
  id: number;
  nombre: string;
  pin_provisorio: number;
}

export interface Yo {
  jugador: { id: number; nombre: string; rol: 'jugador' | 'admin'; elo: number };
  debe_cambiar_pin: boolean;
  es_dueno?: boolean;
}

export interface FilaPuntos {
  id: number;
  nombre: string;
  /** Suma de los meses cerrados de la ventana (los ultimos 12). */
  total: number;
  /** Lo que sumaria si los meses abiertos cerraran hoy. */
  en_juego: number;
  torneos: number;
  /** null = todavia no sumo nada. */
  posicion: number | null;
}

export interface RankingPuntos {
  el_1: number | null;
  meses_que_cuentan: string[];
  meses_en_juego: string[];
  filas: FilaPuntos[];
}

export interface FilaRanking {
  /** null si no jugo en los ultimos 30 dias: va abajo, sin numero. */
  posicion: number | null;
  en_carrera: boolean;
  id: number;
  nombre: string;
  elo: number;
  partidos: number;
  ganados: number;
  perdidos: number;
  games_favor: number;
  games_contra: number;
  provisorio: boolean;
}

export interface JugadorEnPartido {
  id: number;
  nombre: string;
  puesto: number;
  equipo: string;
  elo_despues: number | null;
}

export type MotivoAnulacion = 'duplicado' | 'otro';

export interface Partido {
  id: number;
  jugado_el: string;
  orden: number;
  games_a: number;
  games_b: number;
  tb_a: number | null;
  tb_b: number | null;
  estado: string;
  cargado_por: number;
  /** Aparte de `jugadores`: el que cargó puede ser un admin que no jugó. */
  cargado_por_nombre: string;
  confirmado_por: number | null;
  nota: string | null;
  /** Cuándo se cargó, en segundos unix (no cuándo se jugó: eso es `jugado_el`). */
  creado_en: number;
  /** Por qué se anuló; null si no se dijo (los anulados antes de la 0.6.3). */
  motivo_anulacion: MotivoAnulacion | null;
  jugadores: JugadorEnPartido[];
  puedo_confirmar?: boolean;
  /** Admin que no es rival: puede confirmarlo como tercero neutral. */
  puedo_confirmar_admin?: boolean;
  /** Lo cargó él o su compañero: puede corregir el marcador mientras esté pendiente. */
  puedo_corregir?: boolean;
}

export interface Persona {
  id: number;
  nombre: string;
}

export interface FilaMes {
  posicion: number;
  id: number;
  nombre: string;
  partidos: number;
  ganados: number;
  perdidos: number;
  games_favor: number;
  games_contra: number;
  puntos: number;
  delta_elo: number;
  elegible: boolean;
}

export interface Mes {
  mes: string;
  /** Quién es "El 1" ahora (null = vacante). */
  el_1: number | null;
  titulo: string;
  cerrado: boolean;
  definitivo: boolean;
  se_puede_cerrar: boolean;
  pendientes?: number;
  /** Segundos unix: el 1 del mes siguiente a las 6:00 de Buenos Aires. */
  cierra_en: number;
  meses_con_datos: string[];
  tabla: FilaMes[];
  reconocimientos: {
    campeones: Persona[];
    mas_subio: Persona[];
    mas_presente: Persona[];
  };
}

export interface CrucePerfil {
  id: number;
  nombre: string;
  partidos: number;
  ganados: number;
  perdidos: number;
  esperadas: number;
  sinergia: number;
  poca_muestra: boolean;
}

export interface SeguimientoPerfil {
  titulo: string; desde: string | null;
  partidos: number; ganados: number; perdidos: number;
  games_favor: number; games_contra: number;
  noches: import('../nucleo/perfil').NochePerfil[];
  evolucion: { n: number; fecha: string | null; elo: number }[];
  pico: { elo: number; fecha: string | null };
  forma: boolean[];
  companeros: CrucePerfil[]; rivales: CrucePerfil[];
  companeros_son_ruido: boolean;
}

export interface Perfil {
  seguimiento: Record<import('../nucleo/seguimiento').PeriodoPerfil, SeguimientoPerfil>;
  jugador: {
    id: number;
    nombre: string;
    elo: number;
    posicion: number | null;
    de: number;
    provisorio: boolean;
  };
  partidos: number;
  ganados: number;
  perdidos: number;
  games_favor: number;
  games_contra: number;
  evolucion: { n: number; fecha: string | null; elo: number }[];
  pico: { elo: number; fecha: string | null };
  racha_actual: number;
  mejor_racha: number;
  peor_racha: number;
  forma: boolean[];
  companeros: CrucePerfil[];
  rivales: CrucePerfil[];
  companeros_son_ruido: boolean;
  /** Cómo le va en la tabla del mes en curso; null si este mes no jugó. */
  mes: MesPerfil | null;
  /** Los que consiguió. El catálogo entero (con nombres) vive en nucleo/logros.ts. */
  logros: LogroGuardado[];
  logros_catalogo: FichaLogro[];
  el_1: {
    es_el_1: boolean;
    campeonatos: number;
    /** Desde qué mes hasta qué mes lo tuvo; hasta = null si lo tiene ahora. */
    reinados: Array<{ desde: string; hasta: string | null }>;
  };
}

export interface LogroGuardado {
  logro_id: string;
  /** Qué es depende del logro (ver TipoContexto en nucleo/logros.ts). */
  contexto: string;
  /** Para los de compañero o rival: el nombre de esa persona hoy. */
  contexto_nombre: string | null;
  partido_id: number | null;
  obtenido_en: number;
  origen?: 'automatico' | 'manual';
  entrega_id?: string | null;
  motivo?: string | null;
  otorgado_por_nombre?: string | null;
}

export interface FichaLogro {
  id: string; nombre: string; emoji: string; descripcion: string;
  contexto: import('../nucleo/logros').TipoContexto;
  humor: boolean; origen: 'oficial' | 'automatico_grupo' | 'manual_grupo'; archivado: boolean;
}

export interface LogroAdmin {
  id: string; tipo: 'automatico' | 'manual'; estado: 'borrador' | 'publicado' | 'archivado';
  nombre: string; icono: string; descripcion_manual: string | null; humor: number;
  regla_json: string | null; repeticion: 'ninguna' | 'noche' | 'mes' | 'companero'; revision: number;
  entregas: number; creado_en: number; actualizado_en: number;
}
export interface EntregaLogroAdmin { id:string; jugador_id:number; jugador_nombre:string; contexto:string; fecha:string; motivo:string; revocado_en:number|null; }
export interface DetalleLogroAdmin { logro: LogroAdmin; entregas: EntregaLogroAdmin[]; eventos: Array<{id:string;tipo:string;actor_nombre:string;creado_en:number}>; }

export interface MesPerfil {
  mes: string;
  titulo: string;
  /** null mientras no sea elegible (la tabla mensual muestra un guion). */
  posicion: number | null;
  de: number;
  partidos: number;
  ganados: number;
  perdidos: number;
  puntos: number;
  elegible: boolean;
}

export interface JugadorParaAdmin extends JugadorPublico {
  activo: number;
  rol: 'jugador' | 'admin';
  partidos: number;
  ultimo: string | null;
}

export interface Revision {
  tarde: Array<{ id: number; jugado_el: string; cargado_por_nombre: string; dias: number; estado: string }>;
  rechazos: Array<{ jugador_id: number; nombre: string; rechazados: number; cargados: number }>;
}

export interface FilaPareja {
  posicion: number;
  jugadores: Persona[];
  partidos: number;
  ganados: number;
  perdidos: number;
  games_favor: number;
  games_contra: number;
}

export interface Noche {
  fecha: string;
  partidos: Array<{
    id: number;
    orden: number;
    games_a: number;
    games_b: number;
    jugadores: Array<{ id: number; nombre: string; equipo: string; elo_antes: number; elo_despues: number }>;
  }>;
  movimientos: Array<{
    id: number;
    nombre: string;
    jugados: number;
    ganados: number;
    antes: number;
    despues: number;
  }>;
  figuras: string[];
  logros: Array<{ logro_id: string; nombre: string }>;
  catalogo_logros: FichaLogro[];
  /** Si esta es la primera noche después de que El 1 cambió; nombre null = quedó vacante. */
  el_1_nuevo: { nombre: string | null } | null;
}

/** GET /api/mes/:mes/placa — ver src/worker/rutas/placaMes.ts. */
export interface PlacaMes {
  mes: string;
  titulo: string;
  /** false = el mes no cerró todavía (solo lo ve el admin): la placa sale "provisoria". */
  definitivo: boolean;
  desde: string | null;
  hasta: string | null;
  podio: Array<{
    posicion: number;
    id: number;
    nombre: string;
    puntos: number;
    ganados: number;
    perdidos: number;
    delta_elo: number;
  }>;
  mas_subio: Array<{ id: number; nombre: string; delta_elo: number }>;
  mas_presente: Array<{ id: number; nombre: string; partidos: number }>;
  el_1: { nombre: string | null; cambio: 'primero' | 'nuevo' | 'vacante' | 'sigue' | null };
  numeros: { partidos: number; noches: number; jugadores: number; tiebreaks: number };
  pareja: { nombres: string[]; ganados: number; perdidos: number } | null;
  racha: { nombres: string[]; largo: number } | null;
  siempre: { nombres: string[]; noches: number } | null;
  mas_peleado: Array<{ fecha: string; ganadores: string[]; perdedores: string[]; set: string; tiebreak: string }>;
  logros: Array<{ logro_id: string; nombres: string[]; veces: number }>;
  catalogo_logros: FichaLogro[];
}

/** GET /api/mes/:mes/resumen — la placa más "tu mes" (el de quien pide). */
export interface ResumenMes extends PlacaMes {
  tu_mes:
    | { jugaste: false }
    | {
        jugaste: true;
        /** null = no elegible: jugó pocos partidos para entrar en la corona. */
        posicion: number | null;
        /** Cuántos elegibles hubo. */
        de: number;
        puntos: number;
        partidos: number;
        ganados: number;
        perdidos: number;
        games_favor: number;
        games_contra: number;
        delta_elo: number;
        noches: number;
        noches_del_mes: number;
        racha: number;
        companero: { nombres: string[]; ganados: number } | null;
        logros: string[];
        /** Tu mes anterior; null si no jugaste. */
        anterior: { posicion: number | null; puntos: number } | null;
      };
}

export interface EstadoRespaldo {
  ultimo: number | null;
  clave: string | null;
  bytes: number | null;
  configurado: boolean;
  dias_para_avisar: number;
}

export interface Historial {
  mes: string;
  titulo: string;
  meses_con_datos: string[];
  /** Todos los estados, rechazados incluidos. */
  partidos: Partido[];
}

export interface Marcador {
  games_a: number;
  games_b: number;
  tb_a: number | null;
  tb_b: number | null;
}

export type TipoEvento =
  | 'cargado'
  | 'confirmado'
  | 'confirmado_auto'
  | 'rechazado'
  | 'editado'
  | 'anulado';

export interface EventoPartido {
  tipo: TipoEvento;
  /** null = lo hizo el sistema (confirmado por inactividad). */
  jugador_id: number | null;
  nombre: string | null;
  detalle: {
    como?: string;
    /** confirmado_auto: se confirmó porque cerró el mes, no por el plazo de 1 día. */
    al_cierre?: boolean;
    motivo?: string;
    dias?: number;
    antes?: Marcador;
    despues?: Marcador;
  } | null;
  creado_en: number;
}

export interface PartidoNuevo {
  jugado_el: string;
  equipoA: [number, number];
  equipoB: [number, number];
  games_a: number;
  games_b: number;
  tb_a?: number | null;
  tb_b?: number | null;
  nota?: string | null;
  clave_idem?: string;
  /** Se le avisó que ese día ya hay uno igual y dijo que es otro partido. */
  confirmar_duplicado?: boolean;
}

/** El partido igual que ya estaba cargado, cuando el servidor pregunta. */
export interface Duplicado {
  id: number;
  cargado_por: string;
  estado: string;
  creado_en: number;
}

/** Si el error es "ya hay uno igual", el partido que ya está; si no, null. */
export function duplicadoDe(e: unknown): Duplicado | null {
  if (!(e instanceof ErrorApi) || e.estado !== 409) return null;
  const d = (e.cuerpo as { duplicado?: Duplicado } | null)?.duplicado;
  return d ?? null;
}

// ── La API ────────────────────────────────────────────────────────────────

export const api = {
  salud: () => pedir<Salud>('/salud'),

  paraEntrar: () => pedir<{ jugadores: JugadorPublico[] }>('/jugadores'),
  entrar: (jugador_id: number, pin: string) =>
    postear<{ jugador: Yo['jugador']; debe_cambiar_pin: boolean }>('/auth/entrar', {
      jugador_id,
      pin,
    }),
  salir: () => postear<{ ok: true }>('/auth/salir'),
  yo: () => pedir<Yo>('/auth/yo'),
  cambiarPin: (actual: string, nuevo: string) =>
    postear<{ ok: true }>('/auth/cambiar-pin', { actual, nuevo }),
  cambiarNombre: (nombre: string) =>
    postear<{ ok: true; nombre: string }>('/auth/cambiar-nombre', { nombre }),

  ranking: () => pedir<{ ranking: FilaRanking[]; el_1: number | null }>('/ranking'),
  puntos: () => pedir<RankingPuntos>('/ranking/puntos'),

  perfil: (id: number) => pedir<Perfil>(`/jugadores/${id}/perfil`),

  noche: (fecha: string) => pedir<Noche>(`/noche/${fecha}`),
  parejas: () => pedir<{ minimo: number; parejas: FilaPareja[] }>('/parejas'),

  mes: (cual?: string) => pedir<Mes>(cual ? `/mes/${cual}` : '/mes'),
  cerrarMes: (mes: string) => postear<Mes>(`/mes/${mes}/cerrar`),
  placaMes: (mes: string) => pedir<PlacaMes>(`/mes/${mes}/placa`),
  resumenMes: (mes: string) => pedir<ResumenMes>(`/mes/${mes}/resumen`),

  partidos: () => pedir<{ confirmados: Partido[]; pendientes: Partido[] }>('/partidos'),
  historial: (mes?: string) =>
    pedir<Historial>(mes ? `/partidos/historial/${mes}` : '/partidos/historial'),
  partido: (id: number) =>
    pedir<{ partido: Partido; eventos: EventoPartido[] }>(`/partidos/${id}`),
  cargarPartido: (datos: PartidoNuevo) =>
    postear<{ id: number; estado?: string; repetido?: boolean }>('/partidos', datos),
  confirmar: (id: number) => postear<{ ok: true }>(`/partidos/${id}/confirmar`),
  corregir: (id: number, marcador: Marcador) =>
    postear<{ ok: true }>(`/partidos/${id}/corregir`, marcador),
  rechazar: (id: number, motivo?: string) =>
    postear<{ ok: true }>(`/partidos/${id}/rechazar`, { motivo }),

  jugadoresAdmin: () => pedir<{ jugadores: JugadorParaAdmin[] }>('/admin/jugadores'),
  logrosAdmin: () => pedir<{ logros: LogroAdmin[]; estado: Record<string, unknown> }>('/admin/logros'),
  crearLogro: (datos: Record<string, unknown>) => postear<{ logro: LogroAdmin }>('/admin/logros', datos),
  logroAdmin: (id: string) => pedir<DetalleLogroAdmin>(`/admin/logros/${id}`),
  editarLogro: (id: string, datos: Record<string, unknown>) => postear<{ ok: true }>(`/admin/logros/${id}/editar`, datos),
  previsualizarLogro: (datos: Record<string, unknown>) => postear<{ descripcion: string; total: number; premios: Array<{ nombre: string }> }>('/admin/logros/previsualizar', datos),
  publicarLogro: (id: string, revision_esperada: number, operacion_id: string) => postear<{ ok: true }>(`/admin/logros/${id}/publicar`, { revision_esperada, operacion_id }),
  archivarLogro: (id: string, revision_esperada: number, operacion_id: string) => postear<{ ok: true }>(`/admin/logros/${id}/archivar`, { revision_esperada, operacion_id }),
  otorgarLogro: (id: string, datos: Record<string, unknown>) => postear<{ ok: true; entrega_id: string }>(`/admin/logros/${id}/entregas`, datos),
  revocarEntregaLogro: (id: string, motivo: string, operacion_id: string) => postear<{ ok: true }>(`/admin/logros/entregas/${id}/revocar`, { motivo, operacion_id }),
  actualizarLogros: () => postear<{ actualizado: boolean; premios: number }>('/admin/logros/actualizar'),
  revisar: () => pedir<Revision>('/admin/revisar'),
  respaldo: () => pedir<EstadoRespaldo>('/admin/respaldo'),
  respaldarAhora: () => postear<EstadoRespaldo>('/admin/respaldo'),
  /** No pasa por pedir(): es un archivo, no JSON. El navegador lo descarga. */
  linkRespaldo: () => '/api/admin/respaldo/descargar',
  cambiarActivo: (id: number, activo: boolean) =>
    postear<{ ok: true }>(`/admin/jugadores/${id}/activo`, { activo }),
  altaJugador: (nombre: string) =>
    postear<{ jugador: { id: number; nombre: string }; pin_provisorio: string }>(
      '/admin/jugadores',
      { nombre },
    ),
  renombrarJugador: (id: number, nombre: string) =>
    postear<{ ok: true; nombre: string }>(`/admin/jugadores/${id}/nombre`, { nombre }),
  borrarJugador: (id: number) => postear<{ ok: true }>(`/admin/jugadores/${id}/borrar`),
  resetearPin: (id: number) =>
    postear<{ pin_provisorio: string }>(`/admin/jugadores/${id}/resetear-pin`),
  /** Sobre uno ya anulado sin motivo, completa el motivo (no toca el ranking). */
  anular: (id: number, motivo: MotivoAnulacion) =>
    postear<{ ok: true }>(`/admin/partidos/${id}/anular`, { motivo }),
};

/**
 * Identificador unico del envio.
 *
 * Si el celular reintenta por mala senal en el club, el servidor reconoce esta
 * clave y devuelve el partido que ya existe en vez de cargarlo dos veces.
 */
export function claveDeEnvio(): string {
  return crypto.randomUUID().replace(/-/g, '').slice(0, 32);
}
